/**
 * Passo 2 do portão — por onde o código chega.
 *
 * Os destinos vêm do cadastro do DTP (SGTP), já mascarados pela API: a tela
 * nunca recebe o e-mail nem o celular inteiros (RN-CP-11). Só aparece o canal
 * que existe no cadastro — o que não pode ser escolhido não é renderizado.
 */

import { Info, Loader2, Mail, MessageSquare, Send } from 'lucide-react';
import { useId } from 'react';

import { cn } from '@/nucleo/utilitarios';
import { Button } from '@/ui/base/button';
import { Label } from '@/ui/base/label';
import { RadioGroup, RadioGroupItem } from '@/ui/base/radio-group';

import { type CanalDoCodigo } from './api';
import { AvisoDoPasso } from './AvisoDoPasso';
import { ChipDeResumo } from './ChipDeResumo';
import { type PropsDoPassoDaIdentidade } from './maquinaDeIdentidade';

const ROTULO: Record<CanalDoCodigo, { rotulo: string; icone: typeof Mail }> = {
  EMAIL: { rotulo: 'E-mail', icone: Mail },
  SMS: { rotulo: 'SMS', icone: MessageSquare },
};

export function IdentidadeCanal({ estado, despachar, acoes }: PropsDoPassoDaIdentidade) {
  const idDoGrupo = useId();
  const canais = estado.fluxo?.canais ?? [];

  return (
    <div className="space-y-4">
      {estado.cpf && (
        <div className="flex flex-wrap gap-1.5">
          <ChipDeResumo
            rotulo="CPF"
            valor={`•••.•••.•••-${estado.cpf.slice(-2)}`}
            aoAlterar={() => despachar({ tipo: 'ir_para', passo: 'cpf' })}
          />
        </div>
      )}

      <form
        className="space-y-4"
        onSubmit={(evento) => {
          evento.preventDefault();
          if (!estado.ocupado) void acoes.enviarCodigo();
        }}
      >
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-foreground">
            Como você quer receber o código?
          </legend>
          <RadioGroup
            value={estado.canal}
            onValueChange={(valor) =>
              despachar({ tipo: 'escolher_canal', canal: valor as CanalDoCodigo })
            }
            className="grid grid-cols-1 gap-2"
          >
            {canais.map(({ canal, destino }) => {
              const id = `${idDoGrupo}-${canal}`;
              const { rotulo, icone: Icone } = ROTULO[canal];
              const marcado = estado.canal === canal;
              return (
                <Label
                  key={canal}
                  htmlFor={id}
                  // `normal-case tracking-normal`: o `Label` do design system é
                  // caixa-alta, e endereço de e-mail em caixa-alta é outro texto.
                  className={cn(
                    'flex cursor-pointer items-center gap-2 rounded-xl border p-3 text-sm font-normal normal-case leading-snug tracking-normal text-foreground transition-colors',
                    marcado ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50',
                  )}
                >
                  <RadioGroupItem id={id} value={canal} />
                  <Icone aria-hidden="true" className="h-4 w-4 shrink-0" />
                  {/* O rótulo nunca quebra; o endereço quebra onde precisar,
                      mas aparece inteiro — cortado, a pessoa não reconhece o
                      próprio contato. */}
                  <span className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span className="shrink-0 whitespace-nowrap font-medium">{rotulo}</span>
                    <span className="min-w-0 wrap-break-word font-mono text-muted-foreground">
                      {destino}
                    </span>
                  </span>
                </Label>
              );
            })}
          </RadioGroup>
        </fieldset>

        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <Info aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0" />O código vai para o
          contato cadastrado no DTP e vale por {estado.fluxo?.validadeDoCodigoEmMinutos ?? 10}{' '}
          minutos.
        </p>

        <AvisoDoPasso aviso={estado.aviso} />

        <Button type="submit" className="w-full" disabled={estado.ocupado || canais.length === 0}>
          {estado.ocupado ? (
            <Loader2 aria-hidden="true" className="animate-spin" />
          ) : (
            <Send aria-hidden="true" />
          )}
          Enviar código
        </Button>
      </form>

      <p className="text-xs text-muted-foreground">
        Esse contato não é mais seu?{' '}
        <button
          type="button"
          onClick={() =>
            despachar({
              tipo: 'orientar',
              motivo: estado.canal === 'EMAIL' ? 'EMAIL_DESATUALIZADO' : 'TELEFONE_DESATUALIZADO',
            })
          }
          className="rounded-sm text-link underline underline-offset-2 hover:no-underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
        >
          Veja as opções
        </button>
        .
      </p>
    </div>
  );
}

export default IdentidadeCanal;
